import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import get from 'lodash.get';

// Components
import Error from './Error';
import Loading from './Loading';
import Nav from './Nav/Nav';
import NoPermission from './Recommendations/NoPermission';
import Recommendations from './Recommendations/Recommendations';
import ShareFolderHeader from './Headers/SharedFolderHeader';
import DesignServiceBanner from './DesignServiceBanner/DesignServiceBanner';
import FolderContainer from './Folders/FolderContainer';
import FoldersContainer from './Folders/FoldersContainer';
import FollowingContainer from './Following/FollowingContainer';
import ItemsContainer from './Items/ItemsContainer';
import ProjectsContainer from './Projects/ProjectsContainer';
import { DiscoverContainer } from './Discover/DiscoverContainer';

// Helpers
import actions from '../actions/FavoritesActions';
import { tabs } from '../constants';

import styles from './styles/FavoritesContainer.scss';

export const FavoritesContainerComponent = props => {
    const {
        favoriteItemOnLoad,
        first,
        isLoadingFullPage,
        isLoadingPartialPage,
        portfolioIds,
        portfolioTypes,
        openModalType,
        portfolioDoesNotExist,
        portfolioViewPermission,
        projectId,
        reordered,
        routerNavigation,
        searchTerm,
        selectedItems,
        setModalOpen,
        setMutationResponse,
        setOpenModalType,
        sortBy,
        tab,
        userId,
        hasUserId,
        viewer,
        viewMode,
        isPublic,
    } = props;

    const launchModal = (modalType, mutationResponse = '') => {
        setMutationResponse(mutationResponse);
        setOpenModalType(modalType);
        setModalOpen(true);
    };

    const showProjects = get(viewer, 'user.isVerifiedTrade');
    const showDealerBranding = hasUserId ? get(viewer, 'user.canShowSellerName') : null;

    if (!portfolioViewPermission) {
        return <NoPermission portfolioDoesNotExist={portfolioDoesNotExist} />;
    }

    let tabViewEl;

    const isDiscoverTab = tab === tabs.DISCOVER;
    const selectedItemIds = selectedItems.map(item => item.itemId);
    const containerProps = {
        first,
        isLoadingFullPage,
        hasUserId,
        isLoadingPartialPage,
        launchModal,
        projectId,
        routerNavigation,
        selectedItems,
        selectedItemIds,
        showDealerBranding,
        showProjects,
        sortBy,
        userId,
        userIds: [userId],
        viewer,
        viewMode,
    };

    if (isDiscoverTab) {
        // return discover tab separately because it handles it's own loading state
        return (
            <div className={styles.favoritesContainer}>
                {isPublic ? <ShareFolderHeader /> : <Nav projectsEnabled={showProjects} />}
                <DiscoverContainer
                    viewer={viewer}
                    userId={userId}
                    hasUserId={hasUserId}
                    isLoading={isLoadingFullPage}
                />
            </div>
        );
    } else if (!userId && tab !== tabs.FOLDER && !selectedItemIds.length) {
        tabViewEl = (
            <Recommendations
                favoriteItemOnLoad={false}
                hasUserId={hasUserId}
                portfolioIds={portfolioIds}
                selectedItemIds={selectedItemIds}
                trending={viewer}
                userId={userId}
                viewer={viewer}
            />
        );
    } else if (tab === tabs.FOLDER) {
        tabViewEl = (
            <FolderContainer
                {...containerProps}
                openModalType={openModalType}
                portfolioIds={portfolioIds}
                reordered={reordered}
                searchTerm={searchTerm}
                isPublic={isPublic}
            />
        );
    } else if (tab === tabs.FOLDERS) {
        tabViewEl = (
            <FoldersContainer
                {...containerProps}
                portfolioTypes={portfolioTypes}
                reordered={reordered}
            />
        );
    } else if (tab === tabs.FOLLOWING) {
        tabViewEl = <FollowingContainer {...containerProps} />;
    } else if (tab === tabs.ITEMS) {
        tabViewEl = (
            <ItemsContainer
                {...containerProps}
                openModalType={openModalType}
                favoriteItemOnLoad={favoriteItemOnLoad}
                portfolioTypes={portfolioTypes}
                reordered={reordered}
                searchTerm={searchTerm}
            />
        );
    } else if (showProjects && tab === tabs.PROJECTS) {
        tabViewEl = <ProjectsContainer {...containerProps} />;
    } else if (showProjects && tab === tabs.PROJECT) {
        tabViewEl = (
            <FolderContainer
                {...containerProps}
                openModalType={openModalType}
                portfolioIds={portfolioIds}
                reordered={reordered}
                searchTerm={searchTerm}
                selectedItemIds={selectedItemIds}
                isPublic={isPublic}
            />
        );
    }

    if (viewer) {
        return (
            <div className={styles.favoritesContainer}>
                {<DesignServiceBanner user={viewer.user || null} />}
                <Loading isLoadingFullPage={isLoadingFullPage} />
                {isPublic ? <ShareFolderHeader /> : <Nav projectsEnabled={showProjects} />}
                {tabViewEl}
                {isLoadingPartialPage ? (
                    <Loading partialPageLoading={isLoadingPartialPage} />
                ) : null}
            </div>
        );
    }
    return <Error />;
};

FavoritesContainerComponent.propTypes = {
    favoriteItemOnLoad: PropTypes.bool,
    first: PropTypes.number,
    isLoadingFullPage: PropTypes.bool,
    hasUserId: PropTypes.bool,
    modalOpen: PropTypes.bool,
    mutationResponse: PropTypes.string,
    openModalType: PropTypes.string,
    isLoadingPartialPage: PropTypes.bool,
    portfolioDoesNotExist: PropTypes.bool,
    portfolioIds: PropTypes.array,
    portfolioTypes: PropTypes.array,
    portfolioViewPermission: PropTypes.bool,
    projectId: PropTypes.string,
    reordered: PropTypes.bool,
    routerNavigation: PropTypes.bool,
    searchTerm: PropTypes.string,
    selectedItemIds: PropTypes.array,
    selectedItems: PropTypes.array,
    setModalOpen: PropTypes.func,
    setMutationResponse: PropTypes.func,
    setOpenModalType: PropTypes.func,
    sortBy: PropTypes.string,
    tab: PropTypes.string,
    userId: PropTypes.string,
    viewer: PropTypes.object,
    viewMode: PropTypes.string,
    isPublic: PropTypes.bool,
};

const mapStateToProps = state => {
    return {
        isLoadingFullPage: state.favorites.uiState.isLoadingFullPage,
        reordered: state.favorites.uiState.reordered,
        modalOpen: state.favorites.uiState.modalOpen,
        isPublic: state.favorites.uiState.isPublic,
        isLoadingPartialPage: state.favorites.uiState.isLoadingPartialPage,
        favoriteItemOnLoad: state.favorites.uiState.favoriteItemOnLoad,
        first: state.favorites.uiState.first,
        openModalType: state.favorites.modalState.openModalType,
        portfolioDoesNotExist: state.favorites.uiState.portfolioDoesNotExist,
        portfolioIds: state.favorites.uiState.portfolioIds,
        portfolioViewPermission: state.favorites.uiState.portfolioViewPermission,
        portfolioTypes: state.favorites.uiState.portfolioTypes,
        routerNavigation: state.favorites.uiState.routerNavigation,
        selectedItems: state.favorites.uiState.selectedItems,
        viewMode: state.favorites.uiState.viewMode,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setModalOpen: modalOpen => {
            dispatch(actions.setModalOpen({ modalOpen }));
        },
        setMutationResponse: mutationResponse => {
            dispatch(actions.setMutationResponse({ mutationResponse }));
        },
        setOpenModalType: openModalType => {
            dispatch(actions.setOpenModalType({ openModalType }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(FavoritesContainerComponent);

const FavoritesContainer = createFragmentContainer(ReduxWrapped, {
    viewer: graphql`
        fragment FavoritesContainer_viewer on Viewer {
            user(userId: $userId) @include(if: $hasUserId) {
                ...DesignServiceBanner_user
                isVerifiedTrade
                canShowSellerName
            }
            ... on Viewer {
                ...Recommendations_trending
            }
            ... on Viewer @include(if: $isDiscover) {
                ...DiscoverContainer_viewer
            }
            ... on Viewer @include(if: $isSinglePortfolio) {
                ...FolderContainer_viewer
            }
            ... on Viewer @include(if: $isFolders) {
                ...FoldersContainer_viewer
            }
            ... on Viewer @include(if: $isFollowing) {
                ...FollowingContainer_viewer
            }
            ... on Viewer @include(if: $isItems) {
                ...ItemsContainer_viewer
            }
            ... on Viewer @include(if: $isProjects) {
                ...ProjectsContainer_viewer
            }
        }
    `,
});

export default FavoritesContainer;
